import { LOCATION_CHANGE } from 'connected-react-router';

import { trackUsage } from 'modules/v2/common/utils/trackUsage';
import { resolveRoute } from 'store/usageRoutes';

import {
  SAVE_COVER,
  SAVE_CONTENT,
  SAVE_SECTIONS,
  SAVE_PREFERENCES,
  CREATE_DRAFT,
  RESET_DRAFT,
} from 'modules/editor/store/constants';
import {
  GENERATE_DRAFT_FROM_BOOK,
  GENERATE_DRAFT_FROM_ORDER,
  GENERATE_DRAFT_PREVIEW,
  GENERATE_DRAFT_LINKS,
  GENERATE_DIGITAL_BOOK,
} from 'modules/draft/store/constants';
import { SUBMIT_ORDER, SUBMIT_REORDER } from 'modules/publish/store/constants';

/**
 * Single insertion point for platform usage metrics on the saga-driven side of
 * the app. Only the action types in TRACKED_ACTIONS below emit a RUM event, so
 * the middleware adds no noise and never forwards free text / PII — each
 * `pick(payload)` returns only ids, counts and enum-like values.
 *
 * Mutation/RTK-based features (email campaigns, leads, magazine, billing) do
 * not pass through these constants and are instrumented at their success
 * handlers with `useTrackUsage()` instead.
 *
 * Shape: actionType -> { feature, action, pick?(payload) => safe scalar props }
 */
const TRACKED_ACTIONS = {
  // Editor saves
  [SAVE_COVER]: { feature: 'editor', action: 'save_cover' },
  [SAVE_CONTENT]: { feature: 'editor', action: 'save_content' },
  [SAVE_SECTIONS]: { feature: 'editor', action: 'save_sections' },
  [SAVE_PREFERENCES]: { feature: 'editor', action: 'save_preferences' },
  [CREATE_DRAFT]: { feature: 'editor', action: 'create_draft' },
  [RESET_DRAFT]: { feature: 'editor', action: 'reset_draft' },

  // Draft generation
  [GENERATE_DRAFT_FROM_BOOK]: {
    feature: 'draft',
    action: 'generate',
    pick: (p) => ({ source: 'book', bookId: p?.bookId ?? p?.id }),
  },
  [GENERATE_DRAFT_FROM_ORDER]: {
    feature: 'draft',
    action: 'generate',
    pick: (p) => ({ source: 'order', orderId: p?.orderId ?? p?.id }),
  },
  [GENERATE_DRAFT_PREVIEW]: { feature: 'draft', action: 'preview' },
  [GENERATE_DRAFT_LINKS]: { feature: 'draft', action: 'share_links' },
  [GENERATE_DIGITAL_BOOK]: { feature: 'draft', action: 'generate_digital_book' },

  // Orders / publish book ("Order Books" -> "Complete Order")
  [SUBMIT_ORDER]: {
    feature: 'order',
    action: 'submit',
    pick: (p) => ({ isDigital: p?.isDigital, numberOfPrints: p?.numberOfPrints, isReorder: false }),
  },
  [SUBMIT_REORDER]: {
    feature: 'order',
    action: 'reorder',
    pick: (p) => ({ isDigital: p?.isDigital, numberOfPrints: p?.numberOfPrints, isReorder: true }),
  },
};

const trackingMiddleware = () => (next) => (action) => {
  try {
    if (action?.type === LOCATION_CHANGE) {
      const pathname = action?.payload?.location?.pathname;
      if (pathname) {
        // Emit <feature>.page_view with a normalized pattern + friendly page
        // name so page activity groups per feature and aggregates cleanly.
        const resolved = resolveRoute(pathname);
        trackUsage(resolved.feature, 'page_view', {
          page: resolved.page,
          route_pattern: resolved.route_pattern,
          route: pathname,
        });
      }
    } else {
      const tracked = TRACKED_ACTIONS[action?.type];
      if (tracked) {
        const props = tracked.pick ? tracked.pick(action?.payload) : {};
        trackUsage(tracked.feature, tracked.action, props);
      }
    }
  } catch (err) {
    // Telemetry must never break dispatch.
    // eslint-disable-next-line no-console
    console.error(`Error in trackingMiddleware: ${err}`);
  }

  return next(action);
};

export default trackingMiddleware;
